import { useState, useCallback } from "react";
import { Box, CircularProgress, Typography, Alert } from "@mui/material";
import { useGeospatialProjectsData, GeospatialProject } from "../hooks/useGeospatialProjectsData";
import { MapFilters } from "../components/MapFilters";
import { ProjectInfoCard } from "../components/ProjectInfoCard";
import { MapLegend } from "../components/MapLegend";
import { MapContainer } from "../../geospatial/components/MapContainer";
import { useMapInstance } from "../../geospatial/hooks/useMapInstance";
import { NetworkBoundariesLayer } from "../components/NetworkBoundariesLayer";
import { ProjectClustersLayer } from "../components/ProjectClustersLayer";

export const GeospatialProjects = () => {
  const [selectedProject, setSelectedProject] =
    useState<GeospatialProject | null>(null);

  const {
    projects,
    isLoading: isLoadingData,
    isError,
    error: dataError,
    filters,
    tempFilters,
    updateFilter,
    applyFilters,
    resetFilters,
    hasActiveFilters,
    networkBoundaries,
  } = useGeospatialProjectsData();

  const { mapContainer, map, isLoading: isMapLoading, error: mapError } =
    useMapInstance();

  const handleProjectSelect = useCallback(
    (project: GeospatialProject | null) => {
      setSelectedProject(project);
    },
    []
  );

  const handleCloseProjectCard = useCallback(() => {
    setSelectedProject(null);
  }, []);

  const isLoading = isMapLoading || isLoadingData;

  if (mapError || isError) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
          flexDirection: "column",
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>
            {mapError ? "Map Error" : "Error Loading Projects"}
          </Typography>
          <Typography variant="body2">
            {mapError || dataError || "Failed to load data"}
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        flex: 1,
        width: "100%",
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {isLoading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "background.default",
            zIndex: 2000,
            flexDirection: "column",
            gap: 2,
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="body1" color="text.secondary">
            Loading geospatial projects...
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          height: "100%",
          width: "100%",
          zIndex: 0,
          visibility: !isLoading ? "visible" : "hidden",
        }}
      >
        <MapContainer ref={mapContainer} />
        <NetworkBoundariesLayer map={map} features={networkBoundaries} />
        <ProjectClustersLayer
          map={map}
          projects={projects}
          onProjectSelect={handleProjectSelect}
        />
      </Box>

      <MapFilters
        filters={filters}
        tempFilters={tempFilters}
        updateFilter={updateFilter}
        applyFilters={applyFilters}
        resetFilters={resetFilters}
        hasActiveFilters={hasActiveFilters}
        projectCount={projects.length}
      />

      <ProjectInfoCard
        project={selectedProject}
        onClose={handleCloseProjectCard}
      />

      {!selectedProject && <MapLegend />}

      {projects.length === 0 && !isLoadingData && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            bgcolor: "background.paper",
            p: 4,
            borderRadius: 2,
            boxShadow: 3,
            zIndex: 20,
          }}
        >
          <Typography variant="h6" gutterBottom>
            No Projects Available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {hasActiveFilters
              ? "No projects match the selected filters. Try adjusting your filters."
              : "There are no projects with location data to display on the map."}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
